<script setup lang="ts">
import { BDropdown, BDropdownItem } from 'bootstrap-vue-next';
import type { PropType } from '@vue/runtime-core';

const search = defineModel<string>('search');
const category = defineModel<string>('category');
const sort = defineModel<string>('sort');

const props = defineProps({
  categoryOptions: {
    type: Array as PropType<
      {
        label: string;
        value: any;
      }[]
    >,
  },
  sortOptions: {
    type: Array as PropType<
      {
        label: string;
        value: any;
      }[]
    >,
  },
});

const selectedCategory = computed(() =>
  props.categoryOptions?.find((x) => x.value === category.value),
);

const selectedSort = computed(() =>
  props.sortOptions?.find((x) => x.value === sort.value),
);
</script>

<template>
  <div class="filterBar">
    <slot name="prepend" />
    <ClientOnly>
      <template #fallback>
        <div class="btn-group" v-if="categoryOptions">
          <button class="btn dropdown-toggle" type="button">Category</button>
        </div>
        <div class="btn-group" v-if="sortOptions">
          <button class="btn dropdown-toggle" type="button">Newest</button>
        </div>
      </template>
      <BDropdown
        v-if="categoryOptions"
        :text="selectedCategory?.label || 'Category'"
        :class="{ empty: !selectedCategory }"
        variant="outline-dark"
      >
        <BDropdownItem @click="category = undefined">
          &lt; Clear &gt;
        </BDropdownItem>
        <BDropdownItem
          v-for="option in categoryOptions"
          :key="option.value"
          @click="category = option.value"
        >
          {{ option.label }}
        </BDropdownItem>
      </BDropdown>
      <BDropdown
        v-if="sortOptions"
        :text="selectedSort?.label || 'Sort by'"
        :class="{ empty: !selectedSort }"
        variant="outline-dark"
      >
        <BDropdownItem
          v-for="option in sortOptions"
          :key="option.value"
          @click="sort = option.value"
        >
          {{ option.label }}
        </BDropdownItem>
      </BDropdown>
    </ClientOnly>
    <div class="search-box">
      <svg
        class="icon"
        width="22"
        height="22"
        viewBox="0 0 21 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9.30393 0C14.4397 0 18.6079 4.36138 18.6079 9.73521C18.6079 15.1091 14.4397 19.4704 9.30393 19.4704C4.16816 19.4704 0 15.1091 0 9.73521C0 4.36138 4.16816 0 9.30393 0ZM9.30393 17.307C13.3015 17.307 16.5403 13.9181 16.5403 9.73521C16.5403 5.55124 13.3015 2.16338 9.30393 2.16338C5.30531 2.16338 2.06754 5.55124 2.06754 9.73521C2.06754 13.9181 5.30531 17.307 9.30393 17.307ZM18.0755 17.3838L21 20.4429L19.5372 21.9735L16.6137 18.9134L18.0755 17.3838Z"
          fill="currentColor"
        />
      </svg>
      <input type="search" placeholder="Search news" v-model="search" />
    </div>
    <slot name="append" />
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/css/variables.scss';
@import 'bootstrap/scss/functions';
@import 'bootstrap/scss/variables';
@import 'bootstrap/scss/mixins';

$py: 0.8em;
$px: 1.5em;
$py-md: 0.7em;
$px-md: 1em;

.filterBar {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;

  > * {
    margin: 0 30px 10px 0;
    @include media-breakpoint-down(md) {
      width: 100%;
      margin: 8px 0;
    }
  }
}

::v-deep(.btn) {
  font-size: 14px;
  border: 1px solid black;
  border-radius: 10px;
  padding: $py $px;
  min-width: 150px;
  text-align: left;

  @include media-breakpoint-down(md) {
    padding: $py-md $px-md;
  }
  @include media-breakpoint-down(md) {
    border-radius: 5px;
  }
}

.empty ::v-deep(.btn) {
  --bs-btn-color: #868686;
}

::v-deep(.dropdown-toggle)::after {
  position: absolute;
  right: 1em;
  top: 50%;
  transform: translateY(-50%);
  border-top: 0.5em solid;
  border-right: 0.5em solid transparent;
  border-left: 0.5em solid transparent;
}

.search-box {
  display: flex;
  align-items: center;
  border: 1px solid black;
  border-radius: 10px;

  @include media-breakpoint-down(md) {
    border-radius: 5px;
  }

  .icon {
    margin-left: 14px;
    margin-right: 10px;
  }

  input {
    border: none;
    outline: none;
    padding: $py $px $py 0;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;

    &::placeholder {
      color: #000;
      opacity: 0.5;
    }

    @include media-breakpoint-down(md) {
      padding: $py-md $px-md $py-md 0;
      width: 100%;
    }
  }
}
</style>
